"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import {
  circleActionVariantCssVars,
  resolveCircleActionVariant,
  type CircleActionVariant,
} from "./circle-action-variants";

export interface GlassBadgeProps {
  children: React.ReactNode;
  variant?: CircleActionVariant;
  /** When true: glass tinted with variant colour. When false: neutral glass. */
  active?: boolean;
  size?: "sm" | "md";
  as?: "button" | "div";
  type?: "button" | "submit" | "reset";
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  "aria-label"?: string;
}

export function GlassBadge({
  children,
  variant: variantProp,
  active = false,
  size = "sm",
  as = "button",
  type = "button",
  onClick,
  onMouseEnter,
  onMouseLeave,
  disabled,
  className,
  style: styleProp,
  "aria-label": ariaLabel,
}: GlassBadgeProps) {
  const resolvedVariant = resolveCircleActionVariant(variantProp ?? "slate");
  const variantStyle = circleActionVariantCssVars(resolvedVariant);

  const rootClass = cn(
    "glass-badge",
    "relative inline-flex shrink-0 items-center rounded-full",
    "select-none font-medium tracking-[-0.01em]",
    "motion-safe:[transition:background_340ms_ease,box-shadow_420ms_cubic-bezier(0.16,1,0.3,1),transform_280ms_cubic-bezier(0.16,1,0.3,1),backdrop-filter_340ms_ease,color_240ms_ease]",
    "active:scale-[0.97]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 focus-visible:ring-offset-2",
    "dark:focus-visible:ring-sky-500/40 dark:focus-visible:ring-offset-zinc-950",
    "disabled:pointer-events-none disabled:opacity-40",
    size === "sm"
      ? "gap-1.5 px-3 py-[5px] text-[11px]"
      : "gap-2 px-3.5 py-[7px] text-xs",
    active ? "gb-active" : "gb-idle",
    className
  );

  const content = (
    <span className="relative z-[1] flex items-center gap-1.5">{children}</span>
  );

  const sharedProps = {
    className: rootClass,
    style: { ...(variantStyle as React.CSSProperties), ...styleProp },
    "data-active": active ? "true" : "false",
    "data-variant": resolvedVariant,
  } as const;

  if (as === "div") {
    return <div {...sharedProps}>{content}</div>;
  }

  return (
    <button
      type={type}
      disabled={disabled}
      aria-label={ariaLabel}
      {...sharedProps}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {content}
      <style jsx global>{`
        /* ── IDLE ─────────────────────────────────────────────────────────── */
        .glass-badge.gb-idle {
          isolation: isolate;
          overflow: hidden;
          color: rgb(100 116 139);          /* slate-500 */
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.54) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.36) 38%, rgba(255,255,255,0.08) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.46) 0%, rgba(255,255,255,0.16) 20%, transparent 34%),
            radial-gradient(circle at 50% 50%, rgba(203,213,225,0.18) 0%, rgba(226,232,240,0.08) 100%),
            rgba(226,232,240,0.55);
          backdrop-filter: blur(22px) saturate(1.35);
          -webkit-backdrop-filter: blur(22px) saturate(1.35);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.72),
            inset -1px  0   0 rgba(255,255,255,0.22),
            inset  0   -1px 0 rgba(255,255,255,0.36),
            inset  0    0   0 1px rgba(255,255,255,0.32),
            inset  1px  1px 0 rgba(15,23,42,0.06),
            0 1px 3px rgba(15,23,42,0.06),
            0 4px 12px -4px rgba(15,23,42,0.08);
        }
        .dark .glass-badge.gb-idle {
          color: rgb(161 161 170);          /* zinc-400 */
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.22) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.32) 0%, rgba(255,255,255,0.10) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.05) 20%, transparent 34%),
            radial-gradient(circle at 50% 50%, rgba(148,163,184,0.12) 0%, rgba(100,116,139,0.05) 100%),
            rgba(148,163,184,0.09);
          backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
          -webkit-backdrop-filter: blur(22px) saturate(1.25) brightness(0.96);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.24),
            inset -1px  0   0 rgba(255,255,255,0.10),
            inset  0   -1px 0 rgba(255,255,255,0.16),
            inset  0    0   0 1px rgba(255,255,255,0.14),
            inset  1px  1px 0 rgba(0,0,0,0.22),
            0 1px 3px rgba(0,0,0,0.20),
            0 4px 12px -4px rgba(0,0,0,0.28);
        }

        /* ── HOVER — same tinted glass as active, slightly lighter ──────────── */
        .glass-badge.gb-idle:not(:disabled):hover {
          transform: translateY(-1.5px);
          color: var(--gb-accent-color);
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.62) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0.38) 38%, rgba(255,255,255,0.08) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.50) 0%, rgba(255,255,255,0.18) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(26px) saturate(1.50);
          -webkit-backdrop-filter: blur(26px) saturate(1.50);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.76),
            inset -1px  0   0 rgba(255,255,255,0.24),
            inset  0   -1px 0 rgba(255,255,255,0.40),
            inset  0    0   0 1.5px rgba(255,255,255,0.78),
            inset  1px  1px 0 rgba(15,23,42,0.05),
            0 2px 6px rgba(15,23,42,0.06),
            var(--gb-tint-shadow);
        }
        .dark .glass-badge.gb-idle:not(:disabled):hover {
          color: var(--gb-accent-color-dark);
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.26) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.12) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.06) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(26px) saturate(1.40) brightness(1.02);
          -webkit-backdrop-filter: blur(26px) saturate(1.40) brightness(1.02);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.28),
            inset -1px  0   0 rgba(255,255,255,0.12),
            inset  0   -1px 0 rgba(255,255,255,0.18),
            inset  0    0   0 1.5px rgba(255,255,255,0.32),
            inset  1px  1px 0 rgba(0,0,0,0.18),
            0 2px 6px rgba(0,0,0,0.22),
            var(--gb-tint-shadow);
        }

        /* ── ACTIVE — glass tinted with variant colour ────────────────────── */
        .glass-badge.gb-active {
          isolation: isolate;
          overflow: hidden;
          color: var(--gb-accent-color);
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.50) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.74) 0%, rgba(255,255,255,0.28) 38%, rgba(255,255,255,0.06) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.12) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(22px) saturate(1.55);
          -webkit-backdrop-filter: blur(22px) saturate(1.55);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.68),
            inset -1px  0   0 rgba(255,255,255,0.20),
            inset  0   -1px 0 rgba(255,255,255,0.32),
            inset  0    0   0 1.5px rgba(255,255,255,0.52),
            inset  1px  1px 0 rgba(15,23,42,0.05),
            0 1px 3px rgba(15,23,42,0.06),
            var(--gb-tint-shadow);
        }
        .glass-badge.gb-active:not(:disabled):hover {
          transform: translateY(-1.5px);
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.62) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.86) 0%, rgba(255,255,255,0.38) 38%, rgba(255,255,255,0.08) 58%, transparent 72%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.50) 0%, rgba(255,255,255,0.18) 20%, transparent 34%),
            var(--gb-tint-bg-hover);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.78),
            inset -1px  0   0 rgba(255,255,255,0.28),
            inset  0   -1px 0 rgba(255,255,255,0.42),
            inset  0    0   0 1.5px rgba(255,255,255,0.78),
            inset  1px  1px 0 rgba(15,23,42,0.04),
            0 2px 6px rgba(15,23,42,0.06),
            var(--gb-tint-shadow);
        }
        .dark .glass-badge.gb-active {
          color: var(--gb-accent-color-dark);
          background:
            radial-gradient(ellipse 68% 60% at 24% 18%, rgba(255,255,255,0.20) 0%, transparent 58%),
            radial-gradient(circle at 46% 44%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 40%, transparent 70%),
            radial-gradient(circle at 70% 68%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 20%, transparent 34%),
            var(--gb-tint-bg);
          backdrop-filter: blur(22px) saturate(1.45) brightness(0.98);
          -webkit-backdrop-filter: blur(22px) saturate(1.45) brightness(0.98);
          box-shadow:
            inset -1px -1px 0 rgba(255,255,255,0.22),
            inset -1px  0   0 rgba(255,255,255,0.10),
            inset  0   -1px 0 rgba(255,255,255,0.14),
            inset  0    0   0 1.5px rgba(255,255,255,0.22),
            inset  1px  1px 0 rgba(0,0,0,0.20),
            0 1px 3px rgba(0,0,0,0.20),
            var(--gb-tint-shadow);
        }

        /* ── Icon colour — idle uses variant filter, active/hover inherits ── */
        .glass-badge.gb-idle img,
        .glass-badge.gb-idle svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.85;
          transition: filter 300ms ease, opacity 300ms ease;
        }
        .dark .glass-badge.gb-idle img,
        .dark .glass-badge.gb-idle svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.72;
        }
        .glass-badge.gb-idle:hover img,
        .glass-badge.gb-idle:hover svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.90;
        }
        .dark .glass-badge.gb-idle:hover img,
        .dark .glass-badge.gb-idle:hover svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.85;
        }
        .glass-badge.gb-active img,
        .glass-badge.gb-active svg {
          filter: var(--cab-icon-filter-idle, none);
          opacity: 0.90;
          transition: filter 300ms ease, opacity 300ms ease;
        }
        .dark .glass-badge.gb-active img,
        .dark .glass-badge.gb-active svg {
          filter: var(--cab-icon-filter-idle-dark, none);
          opacity: 0.85;
        }
      `}</style>
    </button>
  );
}
